import { useCallback, useLayoutEffect, useRef } from "react";
import type { Toast } from "@src/services/state/layoutReducer";
import { addToast, clearAllToasts, removeToast } from "@src/services/state/layoutReducer";
import type * as T from "@src/types";
import { useDispatch, useSelector } from "react-redux";

const useLayoutState = () => useSelector((state: T.ReduxState) => state.layout);

const truncateMessage = (message: string, maxLength = 3000): string => {
  if (message.length <= maxLength) {
    return message;
  }
  return message.substring(0, maxLength) + "... (message truncated)";
};

// Hook for components that need to show/hide toasts (no re-renders on toast changes)
export const useToastActions = () => {
  const dispatch = useDispatch();

  // Use refs to maintain stable function references. Written after commit, never during
  // render, so a discarded render can't leak its dispatch into the committed one.
  const dispatchRef = useRef(dispatch);
  useLayoutEffect(() => {
    dispatchRef.current = dispatch;
  });

  const showToast = useCallback((toast: Omit<Toast, "id"> & { id?: string }) => {
    const id = toast.id || Math.random().toString(36).substring(2, 9);
    const fullToast: Required<Toast> = {
      id,
      title: toast.title || "",
      message: truncateMessage(toast.message), // Truncate long messages
      timeout: toast.timeout || 8000, // Increased from 5000 to 8000 (8 seconds)
      type: toast.type || "info",
      actions: toast.actions || [],
    };

    dispatchRef.current(addToast(fullToast));

    // Auto-remove toast after timeout
    setTimeout(() => {
      dispatchRef.current(removeToast(id));
    }, fullToast.timeout);
  }, []);

  const hideToast = useCallback((id: string) => {
    dispatchRef.current(removeToast(id));
  }, []);

  const clearToasts = useCallback(() => {
    dispatchRef.current(clearAllToasts());
  }, []);

  return {
    showToast,
    hideToast,
    clearToasts,
  };
};

// Hook for components that need to display toasts (will re-render on toast changes)
export const useToasts = () => {
  const { toasts } = useLayoutState();
  const { showToast, hideToast, clearToasts } = useToastActions();

  return {
    toasts,
    showToast,
    hideToast,
    clearToasts,
  };
};
